import { useState } from "react";
import { Form, Formik } from "formik";
import plur from "plur";
import { Button, Flex, Label, ModalV2, Text } from "@jsq/ds/core";
import { FormSelect, FormDatePicker } from "@jsq/ds/forms";
import { getMinSelectableDate } from "~/pages/UserAndRoles/utils/dateFormatter";
import {
  useFetchCCToolsUsers,
  useFetchWorkspaceRoles,
} from "~/pages/UserAndRoles/WorkspacesPage/hooks";
import { SPACING } from "~/styles/variables";
import { toGlobalId } from "~/utils/graphql-helpers";
import { WorkspaceData } from "../../types";
import { useAddUserModal } from "./hooks";
import { validationSchema } from "./schema";

export type AddUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedWorkspace?: WorkspaceData;
  selectedWorkspaces?: WorkspaceData[];
};

const AddUserModal = ({
  selectedWorkspace,
  selectedWorkspaces,
  isOpen,
  onClose,
}: AddUserModalProps) => {
  const { initialValues, createWorkspaceAccess, loading } = useAddUserModal({
    arenaId: selectedWorkspace
      ? toGlobalId("ArenaObjectType", selectedWorkspace?.id)
      : undefined,
    arenaIds: selectedWorkspaces?.map((w) => toGlobalId("ArenaObjectType", w.id)),
    closeModal: onClose,
  });
  const { users } = useFetchCCToolsUsers();
  const { roles } = useFetchWorkspaceRoles();
  const [userSelectFocused, setUserSelectFocused] = useState(false);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={createWorkspaceAccess}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, resetForm }) => {
        const handleClose = () => {
          resetForm();
          onClose();
        };

        return (
          <ModalV2
            title="Assign user to workspace"
            description={selectedWorkspace?.name ?? selectedWorkspaces?.length + " workspaces"}
            show={isOpen}
            onHide={handleClose}
            data-testid="add-user-modal"
          >
            <Form>
              <ModalV2.Body>
                <Flex flexDirection="column" gap={SPACING.LARGE}>
                  <FormSelect
                    data-testid="user-select"
                    data-cy="user-select"
                    label="Assignee"
                    name="userIds"
                    placeholder={userSelectFocused ? "Search users..." : "Select user"}
                    full
                    multiple={true}
                    hideSelectAll
                    onFocus={() => setUserSelectFocused(true)}
                    onBlur={() => setUserSelectFocused(false)}
                    matchFromStart={false}
                  >
                    <FormSelect.Section>
                      {users.map((user) => (
                        <FormSelect.Option key={user?.id} value={user?.id}>
                          {`${user?.name} (${user?.email})`}
                        </FormSelect.Option>
                      ))}
                    </FormSelect.Section>
                  </FormSelect>
                  <FormSelect
                    data-testid="role-select"
                    data-cy="role-select"
                    label="Role"
                    name="roleGroupName"
                    placeholder="Select role"
                    full
                    hideSelectAll
                    hideSelectNone
                    matchFromStart={false}
                  >
                    <FormSelect.Section>
                      {roles.map((r) => (
                        <FormSelect.Option key={r?.role} value={r?.role}>
                          {r?.role}
                        </FormSelect.Option>
                      ))}
                    </FormSelect.Section>
                  </FormSelect>
                  <FormSelect
                    data-testid="access-type-select"
                    data-cy="access-type-select"
                    label="Access type"
                    name="accessType"
                    placeholder="Select access type..."
                    full
                    hideSelectAll
                    hideSelectNone
                  >
                    <FormSelect.Section>
                      <FormSelect.Option value="named">Named access</FormSelect.Option>
                      <FormSelect.Option value="unnamed">Anonymous access</FormSelect.Option>
                    </FormSelect.Section>
                  </FormSelect>
                  <Flex flexDirection="column" data-testid="expiration-date-picker">
                    <Label>Expiration date</Label>
                    <FormDatePicker
                      name="expirationDate"
                      hasIcon
                      placeholderText="MM/DD/YYYY"
                      dateFormat={"MM/dd/yyyy"}
                      isFull
                      minDate={getMinSelectableDate()}
                    />
                    <Text variant="secondary-sm">Optional</Text>
                  </Flex>
                  <Flex gap={SPACING.MEDIUM} justifyContent="end">
                    <Button variant="neutral" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      onClick={() => handleSubmit()}
                      variant="primary"
                      isLoading={isSubmitting || loading}
                      disabled={isSubmitting || loading}
                    >
                      {selectedWorkspace
                        ? "Assign to workspace"
                        : `Assign to ${selectedWorkspaces?.length} ${plur("workspace", selectedWorkspaces?.length ?? 0)}`}
                    </Button>
                  </Flex>
                </Flex>
              </ModalV2.Body>
            </Form>
          </ModalV2>
        );
      }}
    </Formik>
  );
};

export default AddUserModal;
